import { useCallback, useEffect, useState } from 'react';
import { ACCENT_TOKENS } from '@shared/accents.ts';
import { api } from '../../lib/api.ts';
import { deckNameError, deckSlugFor, normaliseDeckName, isReservedDeckSlug } from '../../lib/format.ts';
import type { CommandBarProps } from '../../canvas/CommandBar.tsx';
import type { DeckSummary } from '../../../shared/deck.ts';
import type { IngestedFile, IngestSummary } from '../../../shared/api.ts';

/** The wizard's chat transcript — plain messages, no tool cards. */
export type IntakeMessage = { role: 'user' | 'assistant'; content: string };

type SourceDeckInfo = NonNullable<IngestedFile['deck']>;

/** An uploaded presentation the wizard will REBUILD rather than write fresh. */
export type SourceDeck = SourceDeckInfo & { name: string };

/** A fidelity recommendation, with the reason to show the user. Never a lock —
 *  it pre-selects a chip the user can change. */
export type VerbatimAdvice = { value: 'light' | 'strict' | 'source'; reason: string } | null;

/** One eligible upload's picture pass, settled or not. An ARRAY of these rather
 *  than a map keyed by id, because document order is what the grid and every
 *  status line read, and it is the order the author dropped the files in. */
export type SourceDetail = {
  uploadId: string;
  /** The name the AUTHOR knows it by, off the ingest summary — not the
   *  sanitised spelling on disk. Every status line quotes this. */
  name: string;
  /** False once the fetch has settled, however it settled. */
  busy: boolean;
  media: { name: string; page?: number }[];
  headings: string[];
  warnings: string[];
};

/** `a.pdf` · `a.pdf and b.pdf` · `a.pdf, b.pdf and c.pdf`. */
export function listNames(names: string[]): string {
  const last = names.at(-1);
  if (names.length <= 1 || !last) return last || '';
  return `${names.slice(0, -1).join(', ')} and ${last}`;
}

/**
 * What the wizard should SAY about its picture passes, decided here rather than
 * in JSX so the rules can be tested.
 *
 * Three states used to look like two — still working, found none, could not
 * look — and a step that simply never appeared is what sent someone hunting
 * through the server for an afternoon (2026-08-04). Reading several documents
 * adds a fourth way to mislead: saying the same thing N times.
 *
 *  - ONE busy line, never N. Only one document is actually being read at a time
 *    (`queueRenderWork` serialises every Chromium render server-side), so N
 *    lines would be N claims of work where one is true.
 *  - The empty line WAITS for the whole set. Three "no pictures found in X"
 *    appearing one at a time reads as three separate failures rather than one
 *    answer to one question.
 *  - Warnings are named and id-keyed. Everything `source-render.ts` mints
 *    ("Skipped 3 repeated graphics…") names no document, which is unambiguous
 *    with one source and guesswork with three. The id exists because two
 *    documents emitting the identical string is a duplicate React key, and React
 *    would render one — telling the author once about something that happened
 *    twice.
 */
export function sourceStatus(sources: SourceDetail[]): {
  busy: { name: string; position: number } | null;
  total: number;
  empty: string[];
  warnings: { id: string; text: string }[];
} {
  const total = sources.length;
  const at = sources.findIndex((s) => s.busy);
  const working = at === -1 ? undefined : sources[at];
  const busy = working ? { name: working.name, position: at + 1 } : null;
  return {
    busy,
    total,
    // Nothing is claimed while work is outstanding.
    empty: busy ? [] : sources.filter((s) => s.media.length === 0).map((s) => s.name),
    warnings: sources.flatMap((s) =>
      s.warnings.map((w) => ({ id: `${s.uploadId}:${w}`, text: total > 1 ? `${s.name}: ${w}` : w })),
    ),
  };
}

// Intake chip vocabularies shared by both flows.
export const AUDIENCE_CHIPS = [
  { label: 'Internal team', value: 'team' },
  { label: 'Client pitch', value: 'pitch' },
  { label: 'Conference', value: 'conference' },
  { label: 'Workshop', value: 'workshop' },
];
// The named accents, from the one roster. YELLOW is deliberately absent: it
// resolves to the same lime as GREEN, so it read as a duplicate chip — and
// beside LEMON (a real yellow) it would be actively misleading.
export const COLOUR_CHIPS = [...ACCENT_TOKENS];

// Several token names don't match the word someone would type. The blank-deck
// flow accepts free text ("make it yellow"), so the everyday word has to land
// on the right token — otherwise "yellow" silently matches nothing now that the
// YELLOW chip is gone. Note yellow → LEMON, not the lime YELLOW used to give.
const COLOUR_WORDS: Record<string, string> = {
  YELLOW: 'LEMON',
  LIME: 'GREEN',
  VIOLET: 'PURPLE',
  RED: 'SCARLET',
  CRIMSON: 'SCARLET',
  MAGENTA: 'PINK',
  TEAL: 'CYAN',
  TURQUOISE: 'CYAN',
};

/** A typed colour word → a brand token, or null. A real token name always wins;
 *  the synonyms are the fallback. */
export function resolveColourWord(text: string | null | undefined): string | null {
  const t = String(text || '')
    .trim()
    .toUpperCase();
  if (!t) return null;
  const exact = COLOUR_CHIPS.find((c) => t.includes(c));
  if (exact) return exact;
  const word = Object.keys(COLOUR_WORDS).find((w) => t.includes(w));
  return word ? (COLOUR_WORDS[word] ?? null) : null;
}
// Faithfulness to the uploaded brief — worded around fidelity ("keep my words"
// vs "rework"). `light` is the default in the modal: keeps every word but may
// reformat/split. Maps 1:1 to the planner's verbatim modes (plan.ts).
// `label` is the short control text; `hint` is the sentence shown beneath the
// chosen one, so the control stays narrow and the explanation still lands.
export type Verbatim = 'light' | 'strict' | 'source';
export const VERBATIM_CHIPS: { label: string; hint: string; value: Verbatim }[] = [
  {
    label: 'Keep my words',
    hint: 'Keeps your words with light formatting: lines may be reflowed or split to fit, but the wording stays yours.',
    value: 'light',
  },
  { label: 'Word for word', hint: 'Changes nothing: your exact text, word for word.', value: 'strict' },
  { label: 'Adapt & expand', hint: 'Reworks and expands your copy as it sees fit.', value: 'source' },
];

export const DOC_RE = /\.(pdf|md|markdown|txt|docx|pptx|ppsx)$/i;
export const IMG_RE = /\.(png|jpe?g|webp|gif|svg|mp4|webm|mov)$/i;

// Asset-sidecar docs that ride along in a dropped folder but are NOT the brief:
// image credits, licences, readmes. One (`images/SOURCES.md`, H1 "Image sources
// …") once won the title derivation and became the deck slug. Skipped at upload
// so they pollute neither the title nor the content. Matched on basename, plus
// any doc that sits inside an images/assets/media folder (webkitRelativePath).
export const SIDECAR_DOC_RE =
  /^(sources?|credits?|licen[cs]e|attributions?|notice|readme|changelog|contributing)(\.[a-z0-9]+)?$/i;
export function isSidecarDoc(file: { name?: string; webkitRelativePath?: string } | null | undefined): boolean {
  const name = (file?.name || '').trim();
  if (SIDECAR_DOC_RE.test(name)) return true;
  const rel = file?.webkitRelativePath || '';
  return DOC_RE.test(name) && /(^|\/)(images?|img|assets?|media|photos?)\//i.test(rel);
}
// A heading that reads as credits/attribution rather than a real deck title —
// the last-line defence in deriveTitle for a sidecar the upload filter missed.
const CREDITS_TITLE_RE = /^(image\s+)?(sources?|credits?|attributions?|licen[cs]e|references?)\b/i;
// Presentation formats we can't read — caught client-side with the same
// corrective texts the server's 422s use (keep in step with pipeline/ingest.ts).
export const KEY_RE = /\.key$/i;
export const LEGACY_PPT_RE = /\.(ppt|pps)$/i;
export const KEYNOTE_UPLOAD_NOTICE =
  'Keynote files can’t be read here. In Keynote choose File → Export To → PowerPoint (or PDF), then upload the exported file instead.';
export const LEGACY_PPT_UPLOAD_NOTICE =
  '.ppt is the old binary PowerPoint format, which can’t be read here. Open it in PowerPoint and save it as .pptx (or export a PDF), then upload that.';

/** The ingest preview detected an uploaded deck to rebuild (pptx or a PDF that
 *  reads as a slide export). Returns { kind, slideCount, name } or null. */
export function detectSourceDeck(summary: IngestSummary | null | undefined): SourceDeck | null {
  // Narrowed through a type predicate rather than a bare `.filter(f => f.deck)`:
  // `deck` is optional, and the reads below are what make this a rebuild.
  const decks = (summary?.files || []).filter((f): f is IngestedFile & { deck: SourceDeckInfo } => !!f.deck);
  const first = decks[0];
  if (!first) return null;
  return {
    kind: first.deck.kind,
    name: first.name,
    slideCount: decks.reduce((acc, f) => acc + (f.deck.slideCount || 0), 0),
  };
}

// The model a Claude build runs on when the server hasn't said otherwise. The
// wizard's two-chip model picker was removed 2026-08-01 (Jake): deck quality is
// the whole product here and the cost difference is not the deciding factor, so
// it is not a question the AUTHOR is asked per build. It IS a maintainer's
// choice, and since 2026-08-03 it lives with every other engine's build model,
// in the ⚙ drawer's Deck builds section (`claudeBuildModel`, served as
// `build.engines.claude.model`). This constant is only the fallback for a
// wizard that hasn't heard back from the settings endpoint yet.
export const BUILD_MODEL = 'opus';

// The Claude (`claude -p`) engine's model vocabulary. Still the label lookup for
// anything that reports which model built a deck; no UI offers a choice now.
export const CLAUDE_MODELS = [
  {
    label: 'Claude Sonnet',
    value: 'sonnet',
    hint: 'Recommended for almost every deck: matches Opus on standard builds at a fraction of the cost.',
  },
  {
    label: 'Claude Opus (slower, ~5x cost)',
    value: 'opus',
    hint: 'Worth it mainly for very large or complex decks (30+ slides, dense data, intricate layouts). On standard decks Sonnet is just as good.',
  },
];
export function modelLabel(id: string | null | undefined): string | null {
  if (!id) return null;
  return CLAUDE_MODELS.find((m) => m.value === id)?.label || id;
}

/** First markdown heading text across a summary's outlines, joined for scan. */
function summaryText(summary: IngestSummary | null | undefined): string {
  return (summary?.files || []).map((f) => f.outline || f.text || '').join('\n');
}

/**
 * Pull a working title from an ingest summary: the first markdown heading in
 * any file outline, else the first doc's filename de-slugged. Empty string when
 * nothing usable (the modal then leaves the title field for the user to fill).
 */
export function deriveTitle(summary: IngestSummary | null | undefined, docFiles: { name?: string }[] = []): string {
  const heading = summaryText(summary)
    .split('\n')
    .map((l) => l.trim())
    .find((l) => /^#{1,3}\s+\S/.test(l) && !CREDITS_TITLE_RE.test(l.replace(/^#{1,3}\s+/, '')));
  if (heading) {
    const t = heading
      .replace(/^#{1,3}\s+/, '')
      .replace(/[#*\s]+$/, '')
      .trim();
    if (t) return t.slice(0, 120);
  }
  const name = docFiles[0]?.name || summary?.files?.[0]?.name || '';
  const base = name
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (base && !/^pasted.notes$/i.test(base)) return base.charAt(0).toUpperCase() + base.slice(1);
  return '';
}

// (There is no `countSections` any more. Counting a brief's `#`/`##` headings
// guessed at a section plan the author never asked for — on a brief whose own
// section map said "two sections" it pre-filled EIGHT, and because deriving a
// value also flipped the control off "auto", that guess became a hard target
// the build had to hit. Sections now come from the brief itself, decided by the
// agent while it plans — see the wizard modal and the deck-building-claude
// skill's step 1.)

/**
 * Estimate the deck length from the brief's STRUCTURE, not just word count.
 * When an author has split their content into sub-sections (`##`/`###`…) they
 * are usually signalling slide breaks — so a well-sectioned brief reads as
 * ~one slide per sub-section, topped up only when a section is dense enough to
 * need a second. A flat brief with no sub-structure falls back to a word-count
 * estimate. Clamped 5–60. (This is the up-front hint/band; the planner makes
 * the real call, and the plan-review gate lets the user adjust.)
 */
export function estimateSlides(summary: IngestSummary | null | undefined): number {
  // An uploaded deck states its own length — mirror it (the server defaults
  // intake slideCount the same way; 3–60 is the validator band).
  const sourceDeck = detectSourceDeck(summary);
  if (sourceDeck?.slideCount) return Math.max(3, Math.min(60, sourceDeck.slideCount));
  const text = (summary?.files || []).map((f) => f.text || f.outline || '').join('\n');
  // A brief that says "Slide 1 … Slide 6" has stated its own plan outright —
  // believe it before any heuristic. Without this, a plain-text brief with no
  // markdown headings fell through to the bullets rule below, which read the
  // POINTS WITHIN its slides as slides of their own: the six-slide LSF brief
  // (2026-08-07) was estimated at 32 slides, and the fidelity advice then
  // called ~185 words per slide "light" because it divided by 32.
  const slideMarkers = (text.match(/^\s*(?:#{1,6}\s+)?Slide\s+\d+\b/gim) || []).length;
  if (slideMarkers >= 2) return Math.max(3, Math.min(60, slideMarkers));
  const subheads = (text.match(/^#{2,6}\s+\S/gm) || []).length; // `##` and deeper
  const words = (summary?.totalText || text.length || 0) / 5;
  const byWords = Math.round(words / 180);
  // A NOTES-style brief carries its structure in BULLETS, not headings: a
  // handful of `##` sections over a long list of points, and each point becomes
  // a slide. Counting only headings and prose volume read one such brief as
  // "about 5 slides" when every build of it produced 24-27 — so the wizard
  // showed the wrong length, derived the wrong budget from it, and the fidelity
  // advice below was calibrated against a number five times too small.
  // Guarded on FEW headings: a brief with 6+ subheads is already a slide plan
  // (the per-slide outline shape), and its bullets are the content WITHIN each
  // slide, not slides of their own.
  const bullets = (text.match(/^\s*[-*]\s+\S/gm) || []).length;
  const byBullets = subheads < 6 && bullets >= 8 ? Math.round(bullets * 0.9) : 0;
  const est = subheads >= 3 ? Math.max(subheads, Math.round(words / 300)) : byWords;
  return Math.max(5, Math.min(60, Math.max(est, byBullets) || 8));
}

/** slideBudget band from an exact slide count (mirrors the legacy chip rule). */
export function budgetForCount(n: number): 'short' | 'standard' | 'deep' {
  return n <= 15 ? 'short' : n <= 35 ? 'standard' : 'deep';
}

/** Raw words the brief supplies per slide the deck is going to have.
 *  `totalText` is a character count; /5 is the usual English chars-per-word. */
function wordsPerSlide(summary: IngestSummary | null | undefined, targetSlides?: number): number | null {
  const words = Math.round((summary?.totalText || 0) / 5);
  const target = Math.max(1, targetSlides || estimateSlides(summary));
  return words > 0 ? words / target : null;
}

/** Below this, the brief cannot fill the deck it is being asked for without the
 *  agent adding material. Calibrated on real briefs: a written-out deck outline
 *  runs ~150-180 words per slide, and notes-style briefs sit near that too when
 *  the LENGTH follows the content. It only drops when the deck is longer than
 *  the brief supports — which is exactly when "keep my words" produces a deck of
 *  one-line slides with a black band under each. */
const THIN_WORDS_PER_SLIDE = 90;

/**
 * The fidelity setting this brief actually wants, with the reason to show the
 * user. A brief that cannot fill its deck needs "adapt & expand" — under
 * `light`/`strict` the agent may not add anything, so its only lever is
 * emptier slides.
 *
 * A RECOMMENDATION, never a lock: it pre-selects a chip the user can change,
 * and it says why. (The old `countSections` derivation is the cautionary tale —
 * it guessed a number AND made it binding, so a wrong guess silently became a
 * target the build had to hit.)
 */
export function recommendVerbatim(summary: IngestSummary | null | undefined, targetSlides?: number): VerbatimAdvice {
  const per = wordsPerSlide(summary, targetSlides);
  if (per == null) return null;
  if (per < THIN_WORDS_PER_SLIDE) {
    return {
      value: 'source',
      reason:
        `Your brief is light for a deck this long (about ${Math.round(per)} words per slide), so we suggest ` +
        `letting the agent expand it. Keep your exact words instead and the slides will be sparser.`,
    };
  }
  return {
    value: 'light',
    reason: 'Your brief has enough detail to fill the deck, so your own words can carry it.',
  };
}

/**
 * A tiny hook each flow uses to drive the CommandBar chat object: an array of
 * messages plus a busyText "streaming" indicator. Returns the message helpers
 * and a `chat` factory.
 */
export function useIntakeMessages() {
  const [messages, setMessages] = useState<IntakeMessage[]>([]);
  const [busyText, setBusyText] = useState<string | null>(null);
  const say = (text: string) => setMessages((m) => [...m, { role: 'assistant', content: text }]);
  const userSaid = (text: string) => setMessages((m) => [...m, { role: 'user', content: text }]);
  return { messages, setMessages, busyText, setBusyText, say, userSaid };
}

/**
 * The `chat` object both intake flows hand to the CommandBar — the factory the
 * hook above has always claimed to return, and which each flow was writing out
 * by hand instead.
 *
 * It is a chat only in shape: the transcript is this component's own messages,
 * `busyText` stands in for a streaming turn, and the "turn" behind it is a
 * headless build. So there is no `stop` (the bar hides its stop button without
 * one) and `clearThread` is a no-op — there is no server thread to clear.
 */
export function intakeChat(
  messages: IntakeMessage[],
  busyText: string | null,
  send: (text: string) => void,
): CommandBarProps['chat'] {
  return {
    messages,
    streaming: busyText !== null ? { text: busyText, tools: [] } : null,
    status: { kind: 'idle' },
    usage: null,
    send,
    clearThread: () => {},
  };
}

/**
 * Check a proposed deck name the way the server will, at the moment the user
 * types it — pattern first, then a clash against the decks that already exist.
 *
 * The wizard used to silently suffix a colliding slug (`-2`, `-3`), which is how
 * production ended up with two decks both called "RKH AI and digital
 * transformation". Refusing at the naming step is the whole point; refusing
 * AFTER the user has answered five more questions would not be.
 *
 * Returns a hook: `checkName(text)` → an error sentence, or null when free.
 * The deck list is fetched once and is advisory — the server re-checks.
 */
export function useDeckNameCheck() {
  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [slugPrefix, setSlugPrefix] = useState('rkh-');
  useEffect(() => {
    api
      .get<{ decks?: DeckSummary[]; slugPrefix?: string }>('/api/decks')
      .then((data) => {
        setDecks(data.decks || []);
        setSlugPrefix(data.slugPrefix ?? 'rkh-');
      })
      .catch(() => {}); // advisory only — the server is the gate
  }, []);

  return useCallback(
    (raw: string): string | null => {
      const bad = deckNameError(raw);
      if (bad) return bad;
      const name = normaliseDeckName(raw);
      const slug = deckSlugFor(name, slugPrefix);
      if (isReservedDeckSlug(slug)) {
        return `"${name}" is reserved: pick a different name.`;
      }
      const lower = name.toLowerCase();
      for (const d of decks) {
        if (normaliseDeckName(d.title || '').toLowerCase() === lower) {
          return `A deck called "${d.title}" already exists.`;
        }
        if (d.slug === slug) return `That name would clash with the existing deck "${d.title}" (${d.slug}).`;
      }
      return null;
    },
    [decks, slugPrefix],
  );
}
